import { httpsCallable } from "firebase/functions";
import {
  collection,
  collectionGroup,
  onSnapshot,
  orderBy,
  query,
  where
} from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import ProductShell from "../components/ProductShell";
import { useAuth } from "../context/AuthContext";
import { db, functions } from "../firebase";
import { formatPublicHandle } from "../utils/identity";

export default function Team() {
  const { user, userProfile, workspace } = useAuth();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [busyInviteId, setBusyInviteId] = useState("");
  const [sendingInvite, setSendingInvite] = useState(false);

  const isTeamWorkspace = workspace?.planId === "team";
  const isWorkspaceAdmin = ["owner", "admin"].includes(userProfile?.workspaceRole);
  const memberCount = members.filter((member) => member.status === "active").length;
  const seatLimit = workspace?.seatLimit || userProfile?.seatLimit || 1;

  useEffect(() => {
    if (!userProfile?.workspaceId) {
      return undefined;
    }

    const membersQuery = query(
      collection(db, "workspaces", userProfile.workspaceId, "members"),
      orderBy("joinedAt", "asc")
    );

    const invitesQuery = query(
      collection(db, "workspaces", userProfile.workspaceId, "invites"),
      orderBy("createdAt", "desc")
    );

    const unsubscribeMembers = onSnapshot(membersQuery, (snapshot) => {
      setMembers(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    });

    const unsubscribeInvites = onSnapshot(invitesQuery, (snapshot) => {
      setInvites(snapshot.docs.map((item) => ({ id: item.id, ...item.data() })));
    });

    return () => {
      unsubscribeMembers();
      unsubscribeInvites();
    };
  }, [userProfile?.workspaceId]);

  useEffect(() => {
    if (!user?.email) {
      return undefined;
    }

    const pendingQuery = query(
      collectionGroup(db, "invites"),
      where("email", "==", user.email.toLowerCase()),
      where("status", "==", "pending")
    );

    const unsubscribe = onSnapshot(pendingQuery, (snapshot) => {
      setPendingInvites(
        snapshot.docs.map((item) => ({
          id: item.id,
          ...item.data()
        }))
      );
    });

    return unsubscribe;
  }, [user?.email]);

  const openInvites = useMemo(
    () => invites.filter((invite) => invite.status === "pending"),
    [invites]
  );

  const sendInvite = async () => {
    setSendingInvite(true);
    setError("");
    setStatus("");

    try {
      const inviteFn = httpsCallable(functions, "inviteTeamMember");
      await inviteFn({ email });
      setStatus("Invite created.");
      setEmail("");
    } catch (inviteError) {
      setError(inviteError.message || "Unable to create invite.");
    } finally {
      setSendingInvite(false);
    }
  };

  const acceptInvite = async (invite) => {
    setBusyInviteId(invite.id);
    setError("");
    setStatus("");

    try {
      const acceptInviteFn = httpsCallable(functions, "acceptTeamInvite");
      await acceptInviteFn({
        workspaceId: invite.workspaceId,
        inviteId: invite.id
      });
      setStatus(`Joined ${invite.workspaceName}.`);
    } catch (acceptError) {
      setError(acceptError.message || "Unable to accept invite.");
    } finally {
      setBusyInviteId("");
    }
  };

  return (
    <ProductShell
      action={!isTeamWorkspace ? <Link className="cta-button" to="/pricing">Upgrade to Team</Link> : null}
      subtitle={isTeamWorkspace
        ? `This Team workspace has ${seatLimit} seats. ${memberCount} active member${memberCount === 1 ? "" : "s"} are currently using it.`
        : "Team invites are available on the Team plan, which supports one shared workspace for up to 5 users."}
      title={workspace?.name || "Workspace Team"}
    >
      <div className="dashboard-shell product-page">
        {status ? <p className="status success-text mobile-banner">{status}</p> : null}
        {error ? <p className="status error-text mobile-banner">{error}</p> : null}

        {pendingInvites.length ? (
          <section className="card mobile-section-card">
            <h2>Invites for {userProfile?.publicHandle || formatPublicHandle({ email: user?.email })}</h2>
            <div className="link-list">
              {pendingInvites.map((invite) => (
                <article className="link-card mobile-link-card" key={invite.id}>
                  <div>
                    <h3>{invite.workspaceName}</h3>
                    <p className="muted-text">Invited by {invite.invitedByEmail || "workspace admin"}</p>
                  </div>
                  <button
                    className="mobile-action-button"
                    disabled={busyInviteId === invite.id}
                    onClick={() => acceptInvite(invite)}
                    type="button"
                  >
                    {busyInviteId === invite.id ? "Joining..." : "Accept Invite"}
                  </button>
                </article>
              ))}
            </div>
          </section>
        ) : null}

        <section className="dashboard-grid metrics-grid">
          <div className="card mobile-section-card">
            <h2>Members</h2>
            {members.length ? (
              <div className="link-list">
                {members.map((member) => (
                  <article className="link-card mobile-link-card" key={member.id}>
                    <div>
                      <h3>{formatPublicHandle(member, member.email)}</h3>
                      <p className="muted-text">{member.email || member.uid} | {member.role}</p>
                    </div>
                    <span className="mobile-chip">{member.status}</span>
                  </article>
                ))}
              </div>
            ) : (
              <p>No members yet.</p>
            )}
          </div>

          <div className="card mobile-section-card">
            <h2>Invites</h2>
            {isTeamWorkspace && isWorkspaceAdmin ? (
              <>
                <label className="field-label" htmlFor="invite-email">
                  Invite by email
                </label>
                <input
                  id="invite-email"
                  placeholder="mentor-member@example.com"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
                <p className="muted-text">
                  {Math.max(seatLimit - memberCount - openInvites.length, 0)} seat{seatLimit - memberCount - openInvites.length === 1 ? "" : "s"} still open after pending invites.
                </p>
                <button className="mobile-action-button" disabled={sendingInvite} onClick={sendInvite} type="button">
                  {sendingInvite ? "Sending..." : "Send Invite"}
                </button>
              </>
            ) : (
              <p>Only Team workspace admins can send invites.</p>
            )}

            <div className="link-list">
              {invites.length ? (
                invites.map((invite) => (
                  <article className="link-card mobile-link-card" key={invite.id}>
                    <div>
                      <h3>{invite.email}</h3>
                      <p className="muted-text">Role: {invite.role || "member"}</p>
                    </div>
                    <span className="mobile-chip">{invite.status}</span>
                  </article>
                ))
              ) : (
                <p>No invites yet.</p>
              )}
            </div>
          </div>
        </section>
      </div>
    </ProductShell>
  );
}
